import { useCallback } from 'react';
import { useSelector } from '@legendapp/state/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Send } from 'lucide-react';
import { aiConversations$ } from '../../store';
import ReedleMarkdown from '../ReedleMarkdown';
import './LastChatPreview.css';
import { ICON_SIZE } from '../../design-system';

const LastChatPreview = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('articles');

  // Find the most recently updated conversation with messages
  const lastChat = useSelector(() => {
    const conversationsObj = aiConversations$.get() || {};
    let mostRecent = null;

    for (const conv of Object.values(conversationsObj)) {
      if (!conv || conv.deleted) continue;
      const messages = conv.messages;
      if (!Array.isArray(messages) || messages.length === 0) continue;

      if (!mostRecent || (conv.updated_at && conv.updated_at > mostRecent.updated_at)) {
        mostRecent = conv;
      }
    }

    if (!mostRecent) return null;

    const messages = mostRecent.messages;
    // Find last user message and the AI response after it
    let lastUserMsg = null;
    let lastAssistantMsg = null;

    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === 'assistant' && !lastAssistantMsg) {
        lastAssistantMsg = msg;
      }
      if (msg.role === 'user' && !lastUserMsg) {
        lastUserMsg = msg;
        break;
      }
    }

    return {
      articleId: mostRecent.article_id,
      userMessage: lastUserMsg?.content || '',
      assistantMessage: lastAssistantMsg?.content || '',
    };
  });

  const handleContinue = useCallback(() => {
    if (lastChat?.articleId) {
      navigate(`/article/${lastChat.articleId}`);
    }
  }, [lastChat, navigate]);

  const handleSuggestionClick = useCallback(() => {
    // Navigate to the article when a suggestion is clicked
    if (lastChat?.articleId) {
      navigate(`/article/${lastChat.articleId}`);
    }
  }, [lastChat, navigate]);

  if (!lastChat) {
    return (
      <div className="last-chat-preview empty">
        <div className="last-chat-section-label">{t('dashboard.lastChat')}</div>
        <p className="last-chat-empty-text">
          {t('dashboard.lastChatEmpty')}
        </p>
      </div>
    );
  }

  // Truncate user message only (assistant gets markdown rendering)
  const truncate = (text, max) => text.length > max ? text.slice(0, max) + '...' : text;

  // Split assistant message into text portion and suggestions portion
  // so text can be height-limited while suggestions always remain visible
  const splitMessage = (msg) => {
    if (!msg) return { text: '', suggestions: '' };
    const fence = '```reedle:suggestions';
    const idx = msg.indexOf(fence);
    if (idx === -1) return { text: msg, suggestions: '' };
    return { text: msg.slice(0, idx), suggestions: msg.slice(idx) };
  };

  const { text: assistantText, suggestions: assistantSuggestions } = splitMessage(lastChat.assistantMessage);

  return (
    <div className="last-chat-preview">
      <div className="last-chat-section-label">{t('dashboard.lastChat')}</div>
      <div className="last-chat-messages">
        {lastChat.userMessage && (
          <div className="last-chat-message user">
            <div className="last-chat-avatar user">
              <MessageSquare size={ICON_SIZE.xxs} aria-hidden="true" />
            </div>
            <p>{truncate(lastChat.userMessage, 200)}</p>
          </div>
        )}
        {lastChat.assistantMessage && (
          <div className="last-chat-message assistant">
            <div className="last-chat-avatar assistant">{t('dashboard.ai')}</div>
            <div className="last-chat-assistant-content">
              {assistantText && (
                <div className="last-chat-text-body">
                  <ReedleMarkdown enableSuggestions={false}>
                    {assistantText}
                  </ReedleMarkdown>
                </div>
              )}
              {assistantSuggestions && (
                <div className="last-chat-suggestions">
                  <ReedleMarkdown
                    enableSuggestions={true}
                    onSuggestionClick={handleSuggestionClick}
                    suggestionsDisabled={false}
                  >
                    {assistantSuggestions}
                  </ReedleMarkdown>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <button type="button" className="last-chat-continue" onClick={handleContinue}>
        <span>{t('dashboard.continueConversation')}</span>
        <Send size={ICON_SIZE.xs} aria-hidden="true" />
      </button>
    </div>
  );
};

export default LastChatPreview;
